import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FaSearch, FaBox, FaTruck, FaCheck, FaClock, FaEye, FaComments } from 'react-icons/fa';
import { formatPrice } from '../../utils/currency';

export default function SupplierOrders() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (user?.id) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders/supplier/${user.id}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statuses = {
      pending: {
        label: i18n.language === 'fr' ? 'En attente' : 'Pending',
        color: 'bg-[#00D4FF]/10 text-orange-700',
        icon: <FaClock />
      },
      confirmed: {
        label: i18n.language === 'fr' ? 'Confirmée' : 'Confirmed',
        color: 'bg-blue-100 text-blue-700',
        icon: <FaCheck />
      },
      shipped: {
        label: i18n.language === 'fr' ? 'Expédiée' : 'Shipped',
        color: 'bg-purple-100 text-purple-700',
        icon: <FaTruck />
      },
      delivered: {
        label: i18n.language === 'fr' ? 'Livrée' : 'Delivered',
        color: 'bg-[#635BFF]/10 text-[#5248d9]',
        icon: <FaCheck />
      }
    };
    return statuses[status] || statuses.pending;
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.id.toLowerCase().includes(search.toLowerCase()) ||
      order.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      order.user_email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    revenue: orders.reduce((sum, o) => sum + (o.supplier_total || 0), 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-[#0A2540] mb-6">
          {i18n.language === 'fr' ? '📦 Commandes Reçues' : '📦 Orders Received'}
        </h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#0A2540]">{stats.total}</p>
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Total' : 'Total'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'En attente' : 'Pending'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.confirmed}</p>
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Confirmées' : 'Confirmed'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.shipped}</p>
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Expédiées' : 'Shipped'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#635BFF]">{stats.delivered}</p>
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Livrées' : 'Delivered'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#635BFF]">{formatPrice(stats.revenue, 'EUR')}</p>
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Revenus' : 'Revenue'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Input
                  placeholder={i18n.language === 'fr' ? 'Rechercher par n° commande, client...' : 'Search by order #, customer...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{i18n.language === 'fr' ? 'Tous les statuts' : 'All statuses'}</SelectItem>
                  <SelectItem value="pending">{i18n.language === 'fr' ? 'En attente' : 'Pending'}</SelectItem>
                  <SelectItem value="confirmed">{i18n.language === 'fr' ? 'Confirmées' : 'Confirmed'}</SelectItem>
                  <SelectItem value="shipped">{i18n.language === 'fr' ? 'Expédiées' : 'Shipped'}</SelectItem>
                  <SelectItem value="delivered">{i18n.language === 'fr' ? 'Livrées' : 'Delivered'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FaBox className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {i18n.language === 'fr' ? 'Aucune commande trouvée' : 'No orders found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map(order => {
              const statusInfo = getStatusInfo(order.status);
              return (
                <Card key={order.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Order Info */}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg text-[#0A2540]">
                            #{order.id.substring(0, 8).toUpperCase()}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusInfo.color}`}>
                            {statusInfo.icon}
                            {statusInfo.label}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          <p>
                            <span className="font-medium">{i18n.language === 'fr' ? 'Client:' : 'Customer:'}</span>{' '}
                            {order.user_name}
                          </p>
                          <p>
                            <span className="font-medium">Email:</span>{' '}
                            {order.user_email}
                          </p>
                          <p>
                            <span className="font-medium">{i18n.language === 'fr' ? 'Date:' : 'Date:'}</span>{' '}
                            {new Date(order.created_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                          </p>
                          <p>
                            <span className="font-medium">{i18n.language === 'fr' ? 'Articles:' : 'Items:'}</span>{' '}
                            {order.supplier_items?.length || 0}
                          </p>
                        </div>

                        {/* Order Items Preview */}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {order.supplier_items?.slice(0, 3).map((item, idx) => (
                            <span key={idx} className="bg-gray-100 px-2 py-1 rounded text-xs">
                              {item.name} x{item.quantity}
                            </span>
                          ))}
                          {order.supplier_items?.length > 3 && (
                            <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                              +{order.supplier_items.length - 3} {i18n.language === 'fr' ? 'autres' : 'more'}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Price & Actions */}
                      <div className="flex flex-col items-end gap-3">
                        <p className="text-2xl font-bold text-[#0A2540]">
                          {formatPrice(order.supplier_total || 0, 'EUR')}
                        </p>
                        <div className="flex gap-2">
                          <Link to={`/supplier/orders/${order.id}`}>
                            <Button variant="outline" size="sm" className="flex items-center gap-1">
                              <FaEye />
                              {i18n.language === 'fr' ? 'Détails' : 'Details'}
                            </Button>
                          </Link>
                          <Link to={`/supplier/orders/${order.id}`}>
                            <Button size="sm" className="flex items-center gap-1 bg-[#0A2540] hover:bg-[#003d56]">
                              <FaComments />
                              {i18n.language === 'fr' ? 'Messages' : 'Messages'}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
